import React from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

import { useCreateEvacuationForm } from "../../hooks/useCreateEvacuationForm";
import { useAlarmButtonTypes } from "../../hooks/useEvacuationData";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

interface CreateEvacuationPlanSidebarProps {
  onClose: () => void;
}

const SectionLabel: React.FC<{ index: string; children: React.ReactNode }> = ({
  index,
  children,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: BRAND, ml: 0.5 }}>
        *
      </Box>
    )}
  </Typography>
);

const menuPaperProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "12px",
      border: `1px solid ${LINE}`,
      boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
      "& .MuiMenuItem-root": {
        fontSize: "14px",
        fontFamily: '"DM Sans", sans-serif',
        py: 1.25,
        px: 2,
        "&.Mui-selected": {
          background: "rgba(10,22,40,0.04)",
          "&:hover": { background: "rgba(10,22,40,0.06)" },
        },
      },
    },
  },
};

const selectSx = {
  height: 48,
  background: "#fff",
  fontSize: "15px",
  fontWeight: 500,
  fontFamily: '"DM Sans", sans-serif',
  color: INK,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: LINE,
    borderRadius: "12px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.32)",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": { color: INK_MUTED },
};

export const CreateEvacuationPlanSidebar: React.FC<
  CreateEvacuationPlanSidebarProps
> = ({ onClose }) => {
  const { data: alarmTypes, isLoading: isLoadingAlarmTypes } =
    useAlarmButtonTypes();

  const {
    name,
    setName,
    emergencyType,
    setEmergencyType,
    isActive,
    setIsActive,
    steps,
    addStep,
    removeStep,
    updateStep,
    handleSubmit,
    isSubmitting,
  } = useCreateEvacuationForm(onClose);

  const canSubmit =
    !isSubmitting &&
    name.trim().length > 0 &&
    emergencyType.length > 0 &&
    steps.some((s) => s.description.trim().length > 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
        <Box sx={{ mb: 4 }}>
          <SectionLabel index="01">Checklistans identitet</SectionLabel>
          <Stack spacing={2.25}>
            <Box>
              <FieldLabel required>Namn på checklista</FieldLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  height: 48,
                  background: "#fff",
                  border: `1px solid ${LINE}`,
                  borderRadius: "12px",
                  transition: "all 0.25s ease",
                  "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
                  "&:focus-within": {
                    borderColor: "rgba(10,22,40,0.32)",
                    boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
                  },
                }}
              >
                <InputBase
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="T.ex. Checklista vid brand"
                  fullWidth
                  sx={{
                    fontSize: "15px",
                    fontWeight: 500,
                    fontFamily: '"DM Sans", sans-serif',
                    color: INK,
                    "& input::placeholder": { color: INK_MUTED, opacity: 1 },
                  }}
                />
              </Box>
            </Box>

            <Box>
              <FieldLabel required>Kopplad till larmtyp</FieldLabel>
              {isLoadingAlarmTypes ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    height: 48,
                    px: 2,
                    color: INK_MUTED,
                    border: `1px solid ${LINE}`,
                    borderRadius: "12px",
                    background: "#fff",
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: "14px",
                  }}
                >
                  <CircularProgress size={14} sx={{ color: INK_MUTED }} />
                  Läser in larmtyper...
                </Box>
              ) : (
                <FormControl fullWidth>
                  <Select
                    value={emergencyType}
                    onChange={(e) => setEmergencyType(e.target.value as string)}
                    displayEmpty
                    IconComponent={KeyboardArrowDownRoundedIcon}
                    MenuProps={menuPaperProps}
                    sx={selectSx}
                    renderValue={(v) => {
                      if (!v)
                        return (
                          <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                            Välj larmtyp
                          </Box>
                        );
                      return (
                        alarmTypes?.find((t) => t.value === v)?.label || ""
                      );
                    }}
                  >
                    <MenuItem value="" disabled>
                      <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                        Välj larmtyp
                      </Box>
                    </MenuItem>
                    {(alarmTypes || []).map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            <Box>
              <FieldLabel>Status</FieldLabel>
              <Box
                component="button"
                type="button"
                onClick={() => setIsActive(!isActive)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.25,
                  p: 2.25,
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  background: isActive
                    ? "rgba(43,140,77,0.06)"
                    : "#fff",
                  border: `1px solid ${
                    isActive ? "rgba(43,140,77,0.32)" : LINE
                  }`,
                  borderRadius: "14px",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                  fontFamily: '"DM Sans", sans-serif',
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: "#2b8c4d",
                    transform: isActive ? "scaleY(1)" : "scaleY(0)",
                    transformOrigin: "center",
                    transition:
                      "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    minWidth: 44,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isActive
                      ? "linear-gradient(135deg, #2b8c4d, #1a6536)"
                      : "#f4f2ee",
                    color: isActive ? "#fff" : INK_MUTED,
                    transition: "all 0.35s ease",
                    boxShadow: isActive
                      ? "0 10px 22px -10px rgba(43,140,77,0.5)"
                      : "none",
                  }}
                >
                  <BoltRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 600,
                      fontSize: "16px",
                      color: INK,
                      lineHeight: 1.2,
                    }}
                  >
                    Aktiv checklista
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12.5px",
                      color: INK_MUTED,
                      mt: 0.35,
                      lineHeight: 1.4,
                    }}
                  >
                    {isActive
                      ? "Checklistan visas för användare vid larm"
                      : "Checklistan är dold tills den aktiveras"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 42,
                    height: 24,
                    borderRadius: "999px",
                    background: isActive ? "#2b8c4d" : "rgba(10,22,40,0.14)",
                    position: "relative",
                    transition: "background 0.3s ease",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 2,
                      left: 2,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "#fff",
                      boxShadow: "0 2px 6px rgba(10,22,40,0.3)",
                      transform: isActive
                        ? "translateX(18px)"
                        : "translateX(0)",
                      transition:
                        "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <SectionLabel index="02">Steg i checklistan</SectionLabel>
            <Box
              component="button"
              type="button"
              onClick={addStep}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.5,
                height: 32,
                background: "transparent",
                color: BRAND,
                border: `1px solid rgba(138,17,37,0.26)`,
                borderRadius: "999px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.25s ease",
                "&:hover": {
                  background: "rgba(138,17,37,0.06)",
                  borderColor: BRAND,
                },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 15 }} />
              Lägg till steg
            </Box>
          </Box>

          <Stack spacing={1.5}>
            {steps.map((step) => (
              <Box
                key={step.id}
                sx={{
                  border: `1px solid ${LINE}`,
                  borderRadius: "14px",
                  background: "#fff",
                  p: 2.25,
                  position: "relative",
                  transition: "all 0.25s ease",
                  "&:focus-within": {
                    borderColor: "rgba(10,22,40,0.32)",
                    boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "8px",
                        background: `linear-gradient(135deg, ${BRAND}, #c23b54)`,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: '"Outfit", sans-serif',
                        fontWeight: 600,
                        fontSize: "12px",
                        fontVariantNumeric: "tabular-nums",
                        boxShadow: "0 6px 14px -8px rgba(138,17,37,0.55)",
                      }}
                    >
                      {step.order}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "10.5px",
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: INK_MUTED,
                        fontFamily: '"DM Sans", sans-serif',
                      }}
                    >
                      Steg {step.order}
                    </Typography>
                  </Box>
                  {steps.length > 1 && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => removeStep(step.id)}
                      aria-label="Ta bort steg"
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        background: "transparent",
                        border: `1px solid ${LINE}`,
                        color: INK_MUTED,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          background: "rgba(138,17,37,0.06)",
                          borderColor: "rgba(138,17,37,0.3)",
                          color: BRAND,
                        },
                      }}
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                </Box>
                <InputBase
                  multiline
                  minRows={2}
                  maxRows={6}
                  value={step.description}
                  onChange={(e) => updateStep(step.id, e.target.value)}
                  placeholder="Beskriv steget..."
                  fullWidth
                  sx={{
                    fontSize: "14px",
                    fontFamily: '"DM Sans", sans-serif',
                    color: INK,
                    lineHeight: 1.5,
                    "& textarea::placeholder": {
                      color: INK_MUTED,
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          pt: 3,
          mt: 2,
          borderTop: `1px solid ${LINE_SOFT}`,
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onClose}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.25,
            py: 1.25,
            background: "transparent",
            color: INK_MUTED,
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.25s ease",
            "&:hover": {
              color: INK,
              borderColor: "rgba(10,22,40,0.22)",
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
          Avbryt
        </Box>
        <Box
          component="button"
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.75,
            py: 1.25,
            background: INK,
            color: "#fff",
            border: "none",
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: canSubmit ? "pointer" : "not-allowed",
            opacity: canSubmit ? 1 : 0.4,
            transition: "all 0.25s ease",
            boxShadow: canSubmit
              ? "0 10px 24px -12px rgba(10,22,40,0.55)"
              : "none",
            "&:hover": canSubmit
              ? { background: "#1a2638", transform: "translateY(-1px)" }
              : {},
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={14} sx={{ color: "#fff" }} />
          ) : (
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          )}
          Skapa checklista
        </Box>
      </Box>
    </Box>
  );
};
